import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Brain,
  Plus,
  Search,
  Download,
  Heart,
  Play,
  Trash2,
  ExternalLink,
  Sparkles,
  Cpu,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  X,
  ChevronRight,
} from 'lucide-react';

interface Model {
  id: string;
  name: string;
  description?: string;
  baseModel: string;
  task: string;
  status: string;
  likes?: number;
  downloads?: number;
  createdAt: string;
  isPublic?: boolean;
}

const TASK_ICONS: Record<string, typeof Brain> = {
  'text-generation': Sparkles,
  'classification': Cpu,
  'summarization': Zap,
  'translation': Brain,
};

const TASK_COLORS: Record<string, string> = {
  'text-generation': '#f0b90b',
  'classification': '#0ecb81',
  'summarization': '#8b5cf6',
  'translation': '#1e90ff',
};

const STATUS_CONFIG: Record<string, { icon: typeof CheckCircle2; color: string; bg: string }> = {
  ready: { icon: CheckCircle2, color: 'text-[#0ecb81]', bg: 'bg-[#0ecb81]/10' },
  training: { icon: Loader2, color: 'text-[#f0b90b]', bg: 'bg-[#f0b90b]/10' },
  failed: { icon: XCircle, color: 'text-[#f6465d]', bg: 'bg-[#f6465d]/10' },
  draft: { icon: Clock, color: 'text-[#848e9c]', bg: 'bg-white/5' },
};

// Popular Ollama models available for download
const AVAILABLE_OLLAMA_MODELS = [
  { id: 'llama3.2', name: 'Llama 3.2', description: 'Meta\'s latest efficient model', size: '2GB' },
  { id: 'mistral', name: 'Mistral 7B', description: 'High performance 7B model', size: '4GB' },
  { id: 'codellama', name: 'Code Llama', description: 'Specialized for code generation', size: '3.8GB' },
  { id: 'phi3', name: 'Phi-3', description: 'Microsoft\'s efficient small model', size: '2.3GB' },
  { id: 'gemma2', name: 'Gemma 2', description: 'Google\'s lightweight model', size: '5GB' },
  { id: 'qwen2.5', name: 'Qwen 2.5', description: 'Alibaba\'s multilingual model', size: '4GB' },
];

const TASKS = [
  { id: 'text-generation', name: 'Text Generation', description: 'Generate coherent text' },
  { id: 'classification', name: 'Classification', description: 'Categorize text inputs' },
  { id: 'summarization', name: 'Summarization', description: 'Condense long texts' },
  { id: 'translation', name: 'Translation', description: 'Translate between languages' },
];

interface OllamaModel {
  id: string;
  name: string;
  version: string;
  size: number;
  modified: string;
  details?: {
    format: string;
    family: string;
    parameter_size: string;
    quantization_level: string;
  };
}

export default function Models() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'installed' | 'available'>('installed');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pullingModel, setPullingModel] = useState<string | null>(null);
  const [newModel, setNewModel] = useState({ name: '', baseModel: 'llama3.2', task: 'text-generation', description: '' });

  // Fetch installed Ollama models
  const { data: ollamaData, isLoading } = useQuery({
    queryKey: ['ollama-models'],
    queryFn: async () => {
      try {
        const response = await api.get('/ai/models');
        return response.data;
      } catch {
        return { models: [] };
      }
    },
    refetchInterval: 10000,
  });

  // Fetch AI service status
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/ai/status');
        return response.data;
      } catch {
        return { available: false };
      }
    },
  });

  const ollamaModels: OllamaModel[] = ollamaData?.models || [];
  const models: Model[] = ollamaModels.map(m => ({
    id: m.id,
    name: m.name,
    description: m.details?.family ? `${m.details.family} - ${m.details.parameter_size}` : 'Local LLM',
    baseModel: m.name,
    task: 'text-generation',
    status: 'ready',
    createdAt: m.modified,
    isPublic: true,
  }));

  const hubModels: Model[] = [];

  // Pull a new Ollama model
  const pullModelMutation = useMutation({
    mutationFn: async (modelName: string) => {
      setPullingModel(modelName);
      return api.post('/ai/models/pull', { model: modelName });
    },
    onSuccess: () => {
      toast.success('Model pull initiated! This may take a few minutes.');
      queryClient.invalidateQueries({ queryKey: ['ollama-models'] });
      setPullingModel(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to pull model');
      setPullingModel(null);
    },
  });

  const createModelMutation = useMutation({
    mutationFn: (data: any) => api.post('/models', data),
    onSuccess: () => {
      toast.success('Model created successfully');
      queryClient.invalidateQueries({ queryKey: ['ollama-models'] });
      setShowCreateModal(false);
      setNewModel({ name: '', baseModel: 'llama3.2', task: 'text-generation', description: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create model');
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/models/${id}`),
    onSuccess: () => {
      toast.success('Model deleted');
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });

  const displayModels = activeTab === 'installed' ? models : hubModels;
  const filteredModels = displayModels.filter((m) =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">AI Models</h1>
          <p className="text-[#848e9c]">Train and deploy custom AI models</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary btn-md">
          <Plus className="h-5 w-5" />
          New Model
        </button>
      </div>

      {/* Tabs & Search */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-1 p-1 bg-[#181a20] rounded-lg">
          <button
            onClick={() => setActiveTab('installed')}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all",
              activeTab === 'installed' ? "bg-[#2b3139] text-white" : "text-[#848e9c] hover:text-white"
            )}
          >
            My Models
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all",
              activeTab === 'available' ? "bg-[#2b3139] text-white" : "text-[#848e9c] hover:text-white"
            )}
          >
            Model Hub
          </button>
        </div>

        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#848e9c]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search models..."
            className="input pl-11"
          />
        </div>
      </div>

      {/* Models Grid */}
      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-[#848e9c]">Loading models...</p>
        </div>
      ) : filteredModels.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
            <Brain className="h-8 w-8 text-[#848e9c]" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {activeTab === 'installed' ? 'No models yet' : 'No models found'}
          </h3>
          <p className="text-[#848e9c] mb-6 max-w-sm mx-auto">
            {activeTab === 'installed'
              ? 'Create your first AI model to get started with custom training'
              : 'Try adjusting your search query'}
          </p>
          {activeTab === 'installed' && (
            <button onClick={() => setShowCreateModal(true)} className="btn-primary btn-md">
              <Plus className="h-5 w-5" />
              Create Your First Model
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {filteredModels.map((model) => {
            const TaskIcon = TASK_ICONS[model.task] || Brain;
            const taskColor = TASK_COLORS[model.task] || '#848e9c';
            const statusConfig = STATUS_CONFIG[model.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;

            return (
              <div key={model.id} className="card-interactive p-5 group">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: taskColor + '15' }}
                  >
                    <TaskIcon className="h-6 w-6" style={{ color: taskColor }} />
                  </div>
                  <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium', statusConfig.bg)}>
                    <StatusIcon className={cn('h-3.5 w-3.5', statusConfig.color, model.status === 'training' && 'animate-spin')} />
                    <span className={statusConfig.color}>{model.status}</span>
                  </div>
                </div>

                <h3 className="font-semibold text-white mb-1 group-hover:text-[#f0b90b] transition-colors">
                  {model.name}
                </h3>
                <p className="text-sm text-[#848e9c] mb-4 line-clamp-2">
                  {model.description || `Fine-tuned ${model.baseModel} for ${model.task}`}
                </p>

                <div className="flex items-center gap-4 text-xs text-[#848e9c] mb-4">
                  <span className="badge-neutral">{model.baseModel}</span>
                  <span>{model.task}</span>
                </div>

                {activeTab === 'available' && (
                  <div className="flex items-center gap-4 text-xs text-[#848e9c] mb-4">
                    <span className="flex items-center gap-1">
                      <Heart className="h-3.5 w-3.5" />
                      {model.likes || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Download className="h-3.5 w-3.5" />
                      {model.downloads || 0}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-4 border-t border-white/[0.06]">
                  {model.status === 'ready' && (
                    <button className="btn-success btn-xs flex-1">
                      <Play className="h-3.5 w-3.5" />
                      Deploy
                    </button>
                  )}
                  {model.status === 'draft' && (
                    <button className="btn-primary btn-xs flex-1">
                      <Zap className="h-3.5 w-3.5" />
                      Train
                    </button>
                  )}
                  {activeTab === 'installed' && (
                    <button
                      onClick={() => deleteModelMutation.mutate(model.id)}
                      className="btn-ghost btn-xs text-[#f6465d] hover:text-[#f6465d] hover:bg-[#f6465d]/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {activeTab === 'available' && (
                    <button className="btn-secondary btn-xs flex-1">
                      <Download className="h-3.5 w-3.5" />
                      Clone
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content w-full max-w-lg p-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-xl font-bold text-white">Create New Model</h2>
              <button onClick={() => setShowCreateModal(false)} className="btn-ghost p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-2">Model Name</label>
                <input
                  type="text"
                  value={newModel.name}
                  onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                  className="input"
                  placeholder="My Custom Model"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-2">Description</label>
                <textarea
                  value={newModel.description}
                  onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
                  className="input min-h-[80px] resize-none"
                  placeholder="What does this model do?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-3">Base Model</label>
                <div className="grid grid-cols-2 gap-3">
                  {AVAILABLE_OLLAMA_MODELS.map((base) => (
                    <button
                      key={base.id}
                      onClick={() => setNewModel({ ...newModel, baseModel: base.id })}
                      className={cn(
                        "p-4 rounded-xl border text-left transition-all",
                        newModel.baseModel === base.id
                          ? "border-[#f0b90b] bg-[#f0b90b]/5"
                          : "border-white/[0.06] hover:border-white/[0.12]"
                      )}
                    >
                      <div className="font-medium text-white">{base.name}</div>
                      <div className="text-xs text-[#848e9c]">{base.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-3">Task Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {TASKS.map((task) => {
                    const Icon = TASK_ICONS[task.id] || Brain;
                    const color = TASK_COLORS[task.id];
                    return (
                      <button
                        key={task.id}
                        onClick={() => setNewModel({ ...newModel, task: task.id })}
                        className={cn(
                          "p-4 rounded-xl border text-left transition-all",
                          newModel.task === task.id
                            ? "border-[#f0b90b] bg-[#f0b90b]/5"
                            : "border-white/[0.06] hover:border-white/[0.12]"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4" style={{ color }} />
                          <span className="font-medium text-white">{task.name}</span>
                        </div>
                        <div className="text-xs text-[#848e9c]">{task.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#12161c] flex gap-3">
              <button onClick={() => setShowCreateModal(false)} className="btn-secondary btn-md flex-1">
                Cancel
              </button>
              <button
                onClick={() => createModelMutation.mutate(newModel)}
                disabled={!newModel.name || createModelMutation.isPending}
                className="btn-primary btn-md flex-1"
              >
                {createModelMutation.isPending ? 'Creating...' : 'Create Model'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
